"use client";

import Image from "next/image";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUpRight, Check, Copy, Pause, Play, RotateCcw, UserRound } from "lucide-react";
import { t } from "@/content/i18n/messages";
import { getPenangPlan, penangStops, type PenangDuration, type PenangStopId } from "@/content/chatgpt-intelligent-ui";
import type { Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import styles from "./PenangUISimulation.module.css";

const IMAGE_ROOT = "/images/chatgpt-intelligent-ui";
const focus = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;
const stepDelays = [900, 1400, 1800, 1400];

/** Scripted educational example. No chat input, model call or business-system action. */
export function PenangUISimulation({ locale }: { locale: Locale }) {
  const [duration, setDuration] = useState<PenangDuration>(3);
  const [selected, setSelected] = useState<PenangStopId>("heritage");
  const [showDetails, setShowDetails] = useState(false);
  const [view, setView] = useState<"text" | "interactive">("interactive");
  const [stage, setStage] = useState(4);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [visible, setVisible] = useState(true);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "manual">("idle");
  const [replayId, setReplayId] = useState(0);
  const [replayHeight, setReplayHeight] = useState(0);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const answerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLOListElement>(null);
  const previousPositions = useRef(new Map<string, DOMRect>());
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const plan = getPenangPlan(duration);
  const currentStop = penangStops.find((stop) => stop.id === selected)!;
  const number = new Intl.NumberFormat(locale === "zh" ? "zh-MY" : locale === "ms" ? "ms-MY" : "en-MY");
  const prompt = `${t(locale, "Plan a team day in Penang as an interactive answer with pictures, an illustrated route and an editable timeline.")} ${t(locale, "Available time:")} ${number.format(duration)} ${t(locale, "hours")}. ${t(locale, "Let me shorten the plan from six hours to three hours, explain what changed, and let me explore each stop. Label illustrative timings and do not invent venue prices or opening hours.")}`;

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      setReducedMotion(preference.matches);
      if (preference.matches) { setPlaying(false); setStage(4); }
    };
    const visibility = () => setVisible(!document.hidden);
    sync();
    visibility();
    preference.addEventListener("change", sync);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      preference.removeEventListener("change", sync);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);

  useEffect(() => {
    if (!playing || !visible || reducedMotion) return;
    if (stage >= 4) { setPlaying(false); return; }
    const timer = window.setTimeout(() => {
      const next = stage + 1;
      setStage(next);
      if (next === 2) setView("interactive");
      if (next === 4) setDuration(3);
    }, stepDelays[stage]);
    return () => window.clearTimeout(timer);
  }, [playing, visible, reducedMotion, stage]);

  // FLIP: measure once per layout change; animate only transforms on existing nodes.
  useBrowserLayoutEffect(() => {
    const elements = Array.from(timelineRef.current?.querySelectorAll<HTMLElement>("[data-stop]") ?? []);
    const animations: Animation[] = [];
    const positions = new Map<string, DOMRect>();
    for (const element of elements) {
      const id = element.dataset.stop!;
      const box = element.getBoundingClientRect();
      const before = previousPositions.current.get(id);
      positions.set(id, box);
      if (!reducedMotion && before && typeof element.animate === "function") {
        const x = before.left - box.left;
        const y = before.top - box.top;
        if (Math.abs(x) > 1 || Math.abs(y) > 1) animations.push(element.animate([
          { transform: `translate(${x}px, ${y}px)` },
          { transform: "translate(0px, 0px)" },
        ], { duration: 450, easing: "cubic-bezier(0.16, 1, 0.3, 1)" }));
      }
    }
    previousPositions.current = positions;
    return () => animations.forEach((animation) => animation.cancel());
  }, [duration, view, stage, reducedMotion]);

  useEffect(() => setCopyState("idle"), [prompt]);

  function finishInteraction() { setPlaying(false); setStage(4); }

  function changeDuration() {
    finishInteraction();
    setView("interactive");
    setDuration((current) => current === 3 ? 6 : 3);
    setShowDetails(false);
    if (selected === "reflection") setSelected("heritage");
  }

  function replay() {
    setReplayHeight(answerRef.current?.getBoundingClientRect().height ?? 0);
    setReplayId((current) => current + 1);
    setSelected("heritage");
    setShowDetails(false);
    if (reducedMotion) { setDuration(3); setView("interactive"); setStage(4); return; }
    setDuration(6);
    setView("text");
    setStage(0);
    setPlaying(true);
  }

  async function copyPrompt() {
    try { await navigator.clipboard.writeText(prompt); setCopyState("copied"); }
    catch {
      const disclosure = promptRef.current?.closest("details");
      if (disclosure) disclosure.open = true;
      promptRef.current?.focus(); promptRef.current?.select(); setCopyState("manual");
    }
  }

  const assistantMessage = duration === 3
    ? "I have shortened the illustrated plan to three hours."
    : "I have added time for your team to reflect together.";

  return (
    <div className={styles.simulation} data-replaying={playing || stage < 4} data-paused={stage < 4 && !playing} data-motion={reducedMotion ? "off" : "on"}>
      <div className={styles.layout}>
        <div className={styles.conversation} aria-label={t(locale, "Example conversation")}>
          <div className={styles.message}>
            <span className={styles.userAvatar}><UserRound size={21} aria-hidden /></span>
            <div><span className={styles.speaker}>{t(locale, "You")}</span><p className={styles.bubble}>{t(locale, "Plan a team day in Penang.")}</p></div>
          </div>
          <div aria-hidden={stage < 1} className={cn(styles.message, stage < 1 ? styles.pendingMessage : styles.messageEnter)}>
            <Image src={`${IMAGE_ROOT}/robot-avatar.webp`} width={48} height={51} alt="" className={styles.robotAvatar} />
            <div><span className={styles.speaker}>Anchor Sprint</span><p className={styles.bubble}>{t(locale, "Here is a six-hour plan you can explore.")}</p></div>
          </div>
          <div aria-hidden={stage < 3} className={cn(styles.message, stage < 3 ? styles.pendingMessage : styles.messageEnter)}>
            <span className={styles.userAvatar}><UserRound size={21} aria-hidden /></span>
            <div><span className={styles.speaker}>{t(locale, "You")}</span><p className={cn(styles.bubble, styles.followupBubble)}>{t(locale, duration === 3 || stage < 4 ? "We only have three hours." : "Make it six hours again.")}</p></div>
          </div>
          <div aria-hidden={stage < 4} className={cn(styles.message, stage < 4 ? styles.pendingMessage : styles.messageEnter)}>
            <Image src={`${IMAGE_ROOT}/robot-avatar.webp`} width={48} height={51} alt="" className={styles.robotAvatar} />
            <div><span className={styles.speaker}>Anchor Sprint</span><p className={styles.bubble}>{t(locale, assistantMessage)}</p></div>
          </div>
          <div className={styles.followup}>
            <p>{t(locale, "Choose a follow-up to reshape the plan.")}</p>
            <button type="button" onClick={changeDuration} className={cn(styles.outlineButton, focus)}><RotateCcw size={15} aria-hidden />{t(locale, duration === 3 ? "Make it six hours again" : "We only have three hours")}</button>
          </div>
          <p className={styles.disclosure}>{t(locale, "Scripted demo. No live ChatGPT connection.")}</p>
        </div>

        <div ref={answerRef} className={styles.answer} style={stage < 4 ? { minHeight: replayHeight } : undefined}>
          <div className={styles.toolbar}>
            <div className={styles.tabs} role="tablist" aria-label={t(locale, "Compare answer formats")}>
              {(["text", "interactive"] as const).map((format, index) => <button
                key={format} ref={(element) => { tabRefs.current[index] = element; }}
                type="button" id={`penang-tab-${format}`} role="tab" aria-selected={view === format}
                aria-controls="penang-answer-panel" tabIndex={view === format ? 0 : -1}
                onClick={() => { finishInteraction(); setView(format); }}
                onKeyDown={(event) => {
                  if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
                  event.preventDefault();
                  const next = event.key === "Home" ? 0 : event.key === "End" ? 1 : 1 - index;
                  finishInteraction(); setView(next === 0 ? "text" : "interactive"); tabRefs.current[next]?.focus();
                }} className={cn(focus, view === format && styles.activeTab)}
              >{t(locale, format === "text" ? "Text answer" : "Interactive answer")}</button>)}
            </div>
            <div className={styles.playback}>
              {stage < 4 && <button type="button" onClick={() => setPlaying((current) => !current)} className={cn(styles.outlineButton, focus)} aria-pressed={playing}>
                {playing ? <Pause size={15} aria-hidden /> : <Play size={15} aria-hidden />}{t(locale, playing ? "Pause demo" : "Resume demo")}
              </button>}
              <button type="button" onClick={replay} className={cn(styles.outlineButton, focus)}><RotateCcw size={15} aria-hidden />{t(locale, "Replay changes")}</button>
            </div>
          </div>
          <div id="penang-answer-panel" role="tabpanel" aria-labelledby={`penang-tab-${view}`} tabIndex={0} className={focus}>
            <div className={styles.excerpt}>
              <p className={styles.eyebrow}>{t(locale, view === "text" ? "A useful text answer" : "The same answer, made visual")}</p>
              <p>{t(locale, "Meet over local food, explore the heritage streets, then finish by the waterfront.")}</p>
            </div>
            {stage === 0 ? <div className={styles.textAnswer}><p>{t(locale, "Start with a question. Watch how the answer takes shape.")}</p></div> : view === "text" ? <div className={styles.textAnswer}>
              <h3>{t(locale, "Penang team day plan")}</h3>
              <ol>{plan.stops.map((stop) => <li key={stop.id}><strong>{stop.time} · {t(locale, stop.title)}</strong><p>{t(locale, stop.description)}</p></li>)}</ol>
              <p>{plan.finish} · {t(locale, "Finish")} — {number.format(duration)} {t(locale, "hours")}</p>
              <p className={styles.timing}>{t(locale, "Illustrative timing; travel included. No verified venue bookings.")}</p>
              <button type="button" onClick={() => { finishInteraction(); setView("interactive"); }} className={cn(styles.primaryButton, focus)}><Play size={15} aria-hidden />{t(locale, "Make this answer visual")}</button>
            </div> : <div className={styles.visualAnswer}>
              <div className={styles.planHeading}>
                <h3>{t(locale, "Penang team day plan")}</h3>
                <span className={styles.update}>{t(locale, stage < 4 ? "Initial six-hour plan" : duration === 3 ? "Updated: 6 hours → 3 hours" : "Expanded: 3 hours → 6 hours")}</span>
              </div>
              <div className={styles.planSummary}>
                <div><p>{number.format(duration)} {t(locale, "hours")} · {number.format(plan.stops.length)} {t(locale, "stops")}</p><p className={styles.timing}>{t(locale, "Illustrative timing; travel included.")}</p></div>
                <p className={styles.timing}>{t(locale, "Tap a stop to explore the plan.")}</p>
              </div>
              <div className={styles.map}>
                <div key={replayId} className={styles.mapReveal}><Image src={`${IMAGE_ROOT}/penang-map.webp`} alt={t(locale, "Illustrated Penang heritage streets and waterfront with a blue route; not a navigation map.")} width={1600} height={544} sizes="(min-width: 1024px) 850px, 95vw" className={styles.mapImage} /></div>
                <span className={styles.mapCaption}>{t(locale, "Illustrated route")}</span>
                {plan.stops.map((stop, index) => <button key={stop.id} data-stop-id={stop.id} type="button" aria-label={`${index + 1}. ${t(locale, stop.title)}`} aria-pressed={selected === stop.id} aria-controls="penang-stop-details" onClick={() => { finishInteraction(); setSelected(stop.id); setShowDetails(true); }} style={{ left: `${stop.pin[0]}%`, top: `${stop.pin[1]}%` }} className={cn(styles.pin, selected === stop.id && styles.selectedPin, focus)}>{number.format(index + 1)}</button>)}
              </div>
              <ol ref={timelineRef} className={styles.timeline} data-count={plan.stops.length}>
                {plan.stops.map((stop, index) => <li key={stop.id} data-stop={stop.id} className={styles.stopEnter}>
                  <button type="button" onClick={() => { finishInteraction(); setSelected(stop.id); setShowDetails(true); }} aria-pressed={selected === stop.id} aria-controls="penang-stop-details" className={cn(styles.stopButton, selected === stop.id && styles.selectedStop, focus)}>
                    <span className={styles.stopHeading}><span className={styles.stopNumber}>{number.format(index + 1)}</span><span><span className={styles.stopTime}>{stop.time}</span><strong>{t(locale, stop.title)}</strong></span>{selected === stop.id && <Check size={15} aria-label={t(locale, "Selected stop")} className="ml-auto shrink-0 text-primary" />}</span>
                    <Image src={`${IMAGE_ROOT}/penang-${stop.id === "food" ? "food" : stop.id}.webp`} width={640} height={320} alt={t(locale, stop.alt)} sizes="(min-width: 1024px) 260px, (min-width: 640px) 30vw, 85vw" className={styles.stopImage} />
                    <span className={styles.stopDescription}>{t(locale, stop.description)}</span>
                  </button>
                </li>)}
              </ol>
              <div className={styles.finish}><span>{plan.finish} · {t(locale, "Finish")}</span><span>{t(locale, "Pictures and route are illustrative.")}</span></div>
              <div id="penang-stop-details" className={styles.details} hidden={!showDetails} role="status" aria-live="polite">
                <strong>{t(locale, currentStop.title)}</strong><p>{t(locale, currentStop.detail)}</p>
              </div>
            </div>}
          </div>
          <p className={styles.changeNote} role="status" aria-live="polite">{stage < 4 ? t(locale, "Watch the question become a visual answer, then change after a follow-up.") : t(locale, duration === 3 ? "Shorter plan: the reflection stop is removed and the remaining times are adjusted." : "Longer plan: a reflection stop is added and the timeline expands.")}</p>
        </div>
      </div>
      <div className={styles.promptFooter}>
        <details className={styles.promptDetails}><summary className={focus}>{t(locale, "View the example prompt")}</summary><textarea ref={promptRef} aria-label={t(locale, "Example prompt for ChatGPT")} value={prompt} readOnly rows={4} className={cn(styles.promptText, focus)} /></details>
        <div className={styles.promptActions}>
          <div className={styles.promptButtons}>
            <button type="button" onClick={copyPrompt} className={cn(styles.primaryButton, focus)}>{copyState === "copied" ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}{t(locale, copyState === "copied" ? "Prompt copied" : "Copy prompt")}</button>
            <a href="https://chatgpt.com/" target="_blank" rel="noopener noreferrer" className={cn(styles.outlineButton, focus)}>{t(locale, "Open ChatGPT")}<ArrowUpRight size={15} aria-hidden /><span className="sr-only">{t(locale, "opens in a new tab")}</span></a>
          </div>
          <p role="status">{t(locale, copyState === "manual" ? "The prompt is selected. Copy it manually, then paste it into ChatGPT." : copyState === "copied" ? "Copied. Open ChatGPT and paste it into a new chat." : "Copy the prompt, then paste it into ChatGPT.")}</p>
        </div>
      </div>
    </div>
  );
}
